import { createApp, watch, nextTick, ref } from 'vue'
import { createPinia } from 'pinia'
import Vue3Toastify, { toast } from 'vue3-toastify'
import VueTelInput from 'vue3-tel-input'
import VOtpInput from 'vue3-otp-input'
import 'vue3-tel-input/dist/vue3-tel-input.css'
import * as Sentry from '@sentry/vue'
import Vue3PersianDatetimePicker from 'vue3-persian-datetime-picker'
import App from './App.vue'
import router from './router'
import axios from 'axios'
import VueAxios from 'vue-axios'
import VueCookies from 'vue3-cookies'
import settings from './settings'
import '../sw'
import PN from 'persian-number/src/persianNumber'
import { createI18n } from 'vue-i18n'
import 'viewerjs/dist/viewer.css'
import VueViewer from 'v-viewer'
import en from './locales/en.json'
import fa from './locales/fa.json'
import { createGtag } from 'vue-gtag'
import { useWindowScroll, useElementSize } from '@vueuse/core'
import { User } from '@/logic/users/user/user'

let qs = require('qs')

const app = createApp(App)

const savedLocale = localStorage.getItem('user-locale') || 'en'

export const i18n = createI18n({
  locale: savedLocale,
  fallbackLocale: 'en',
  messages: {
    en: en,
    fa: fa
  },
  silentTranslationWarn: true,
  silentFallbackWarn: true
})

app.use(VueViewer, {
  defaultOptions: {
    zoomRatio: 0.4
  }
})

app.use(i18n)

const gtag = createGtag({
  tagId: 'G-L58YEX5JK6',
  pageTracker: { router },
  config: {
    params: { debug_mode: false }
  }
})

app.use(Vue3Toastify, {
  autoClose: 3000,
  position: toast.POSITION.TOP_CENTER,
  theme: toast.THEME.COLORED
})
app.use(VueTelInput)

const localeDirections = {
  fa: 'rtl',
  en: 'ltr'
}

watch(
  () => i18n.global.locale,
  (newLocale) => {
    const direction = localeDirections[newLocale] || 'ltr'
    document.documentElement.setAttribute('dir', direction)
  },
  { immediate: true }
)

app.use(Vue3PersianDatetimePicker, {
  name: 'CustomDatePicker',
  props: {
    format: 'YYYY-MM-DD HH:mm',
    displayFormat: 'jYYYY-jMM-jDD',
    editable: false,
    inputClass: 'form-control my-custom-class-name',
    placeholder: 'Please Select a date',
    altFormat: 'YYYY-MM-DD HH:mm',
    color: '#00acc1',
    autoSubmit: false
  }
})

let axiosRequestHandler = (config) => {
  const { $cookies } = app.config.globalProperties
  // Add a custom header to the request
  config.headers['Authorization'] = 'Bearer ' + $cookies.get('token')
  return config
}

let axiosErrorHandler = function (error) {
  if (error.name === 'AbortError' || axios.isCancel(error)) {
    console.log('Request was aborted. Ignoring error.')
    return Promise.reject(error)
  }

  const resp = error.response || null
  if (resp && resp.status === 429) {
    try {
      window.dispatchEvent(new CustomEvent('api-429'))
    } catch (e) {
      const evt = document.createEvent('Event')
      evt.initEvent('api-429', true, true)
      window.dispatchEvent(evt)
    }
    toast.error('Too many requests. Please try again later.')
  }

  if (error.config.errorHandling === false) {
  } else if (error.code === 'ERR_NETWORK') {
    toast.error('Network Error!')
  } else if (error.code === 'ECONNABORTED') {
    toast.error('Request Timeout!')
  } else if (error.response.status === 401) {
    const { $cookies } = app.config.globalProperties
    $cookies.remove('token')
    if (router.currentRoute.value.path !== '/' && router.currentRoute.value.meta.needAuth !== false)
      router.push({ name: 'Login' })
  } else if (error.response.status >= 500 && error.response.status < 600) {
    toast.error('Server Error!')
  } else if (
    error.response.data instanceof Object &&
    'code' in error.response.data &&
    'ignoreErrorCodes' in error.config &&
    error.config.ignoreErrorCodes.includes(error.response.data.code)
  ) {
  } else if ([400, 402, 403, 404, 405, 406].includes(error.response.status)) {
    if (error.response.data.message) {
      toast.error(error.response.data.message, {
        position: toast.POSITION.TOP_CENTER,
        theme: toast.THEME.COLORED,
        toastId: error.response.data.message
      })
    }
  }
  return Promise.reject(error)
}

axios.interceptors.response.use((response) => response, axiosErrorHandler)

axios.interceptors.request.use(axiosRequestHandler)

const axios_include_timezone = axios.create()
app.config.globalProperties.axios_include_timezone = axios_include_timezone

axios_include_timezone.interceptors.request.use((config) => {
  var config = axiosRequestHandler(config)
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  config.headers['UserTimeZone'] = userTimeZone
  config.params = {
    ...config.params,
    UserTimeZone: userTimeZone
  }
  return config
})

axios_include_timezone.interceptors.response.use((response) => response, axiosErrorHandler)

const isMobileRef = ref(typeof window !== 'undefined' ? window.innerWidth < 480 : false)

if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => {
    const nowIsMobile = window.innerWidth < 480
    if (isMobileRef.value !== nowIsMobile) {
      isMobileRef.value = nowIsMobile
    }
  })
}

//todo: the isMobile and isDesktop are true and false because the desktop view is in development phase.

export const filters = {
  get isMobile() {
    // return isMobileRef.value
    return true
  },
  get isDesktop() {
    // return !isMobileRef.value
    return false
  },
  persianToEnglishDigits(txt) {
    if (!(typeof txt == 'string')) return txt
    let persian_digits_map = {
      '۱': '1',
      '۲': '2',
      '۳': '3',
      '۴': '4',
      '۵': '5',
      '۶': '6',
      '۷': '7',
      '۸': '8',
      '۹': '9',
      '۰': '0'
    }

    let new_txt = ''

    for (let c of txt) {
      if (c in persian_digits_map) new_txt = new_txt + persian_digits_map[c]
      else new_txt = new_txt + c
    }

    return new_txt
  },
  EnglishToPersianDigits(txt) {
    if (!(typeof txt == 'string')) txt = txt.toString()
    let persian_digits_map = {
      1: '۱',
      2: '۲',
      3: '۳',
      4: '۴',
      5: '۵',
      6: '۶',
      7: '۷',
      8: '۸',
      9: '۹',
      0: '۰'
    }
    let new_txt = ''
    for (let c of txt) {
      if (c in persian_digits_map) new_txt = new_txt + persian_digits_map[c]
      else new_txt = new_txt + c
    }
    return new_txt
  },
  formatPhoneNumber(phoneNumber) {
    if (phoneNumber == null) return null

    return phoneNumber.replace('+98', '0')
  },
  convertNumberToMillions(number) {
    let num = parseFloat(number)
    if (isNaN(num)) return number
    const million = 1000000
    let formattedNumber = Math.floor(num / million)
    return this.prettyNumber(formattedNumber.toString())
  },

  prettyPositiveInteger(number) {
    number = number.toString()
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  },
  prettyInteger(number) {
    number = number.toString()
    if (number.includes('-')) {
      number = number.replace('-', '')
      return '-' + app.config.globalProperties.$filters.prettyPositiveInteger(number)
    }
    return app.config.globalProperties.$filters.prettyPositiveInteger(number)
  },
  prettyNumber(number) {
    number = number.toString()
    if (number.includes('.')) {
      var parts = number.toString().split('.')
      parts[0] = app.config.globalProperties.$filters.prettyInteger(parts[0])
      return parts[1] === '' ? parts[0] : parts.join('.')
    }
    return app.config.globalProperties.$filters.prettyInteger(number)
  },
  formatDecimal(decimal, digitAfterDecimal = 8, floor = false) {
    if (!decimal && decimal !== 0) return ''
    if (decimal === null || decimal === '') return ''
    if (floor) {
      const factor = Math.pow(10, digitAfterDecimal)
      return Math.floor(parseFloat(decimal) * factor) / factor
    }
    return Number(parseFloat(decimal).toFixed(digitAfterDecimal))
  },
  prettyAndFormatDecimal(decimal, digitAfterDecimal = 1, floor = false) {
    return this.prettyNumber(this.formatDecimal(decimal, digitAfterDecimal, floor))
  },
  formatNumberString(numberStr, allowedDecimals = 20) {
    if (!numberStr.includes('.')) return numberStr

    let decimalPlaces = numberStr.length - numberStr.indexOf('.') - 1
    if (decimalPlaces > allowedDecimals)
      return numberStr.slice(0, -(decimalPlaces - allowedDecimals))
    return numberStr
  },
  numberToString(number) {
    let newValue = number.toString()
    if (newValue.includes('e')) {
      let pw = -Number(newValue.split('e')[1])
      let meanPart = newValue.split('e')[0]
      newValue = this.move(meanPart, pw)
    }
    return newValue
  },
  truncateDecimal(decimalStr, decimalPlaces) {
    if (!decimalStr.includes('.')) return decimalStr

    return decimalStr.slice(0, decimalStr.indexOf('.') + decimalPlaces + 1)
  },
  truncateIntegerPart(decimalStr, integerPartLength) {
    if (decimalStr.includes('.'))
      return (
        this.truncateIntegerPart(decimalStr.slice(0, decimalStr.indexOf('.')), integerPartLength) +
        decimalStr.slice(decimalStr.indexOf('.'))
      )

    if (decimalStr.length <= integerPartLength) return decimalStr
    return decimalStr.slice(decimalStr.length - integerPartLength)
  },
  decimalPartLength(decimalStr) {
    if (!decimalStr.includes('.')) return 0

    return decimalStr.slice(decimalStr.indexOf('.') + 1).length
  },
  integerPartLength(decimalStr) {
    if (decimalStr.includes('.'))
      return this.integerPartLength(decimalStr.slice(0, decimalStr.indexOf('.')))

    return decimalStr.length
  },
  move(number, position) {
    let res = ''
    if (position > 0) {
      res = this.moveRight(number, position)
    } else res = this.moveLeft(number, -position)

    while (res.includes('.') && (res.slice(-1) == '0' || res.slice(-1) == '.'))
      res = res.slice(0, -1)

    return res
  },
  moveRight(number, position) {
    if (!number.includes('.')) number = number + '.0'

    while (position) {
      let pointPosition = number.indexOf('.')
      let next = number[pointPosition - 1]
      number = this.replaceAt(number, pointPosition, next)
      number = this.replaceAt(number, pointPosition - 1, '.')

      if (number[0] == '.') number = '0' + number
      position -= 1
    }

    return number
  },
  moveLeft(number, position) {
    if (!number.includes('.')) number = number + '.0'

    while (position) {
      let pointPosition = number.indexOf('.')
      let next = number[pointPosition + 1]
      number = this.replaceAt(number, pointPosition, next)
      number = this.replaceAt(number, pointPosition + 1, '.')
      if (number.slice(-1) == '.') number = number + '0'
      position -= 1
    }
    return number
  },
  replaceAt(str, index, replacement) {
    return str.substring(0, index) + replacement + str.substring(index + replacement.length)
  },
  removeDecimalPart(decimal) {
    return Math.trunc(decimal)
  },
  mask(text, maxLength = 7, maxStarCount = 5) {
    if (text === undefined || text === null) return text
    // if (text.length <= maxLength * 2 + maxStarCount) return text
    let result = ''
    let starCount = 0
    for (let i = 0; i < text.length; i++) {
      if (i < maxLength) result += text[i]
      else if (i >= text.length - maxLength) result += text[i]
      else if (starCount < maxStarCount) {
        result += '*'
        starCount += 1
      }
    }
    return result
  },
  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  },
  camelize(str) {
    let camelCase = str
      .toLowerCase()
      .replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''))
    return this.capitalize(camelCase)
  },
  camelToSnakeCase(str) {
    var result = str.replace(/([A-Z])/g, ' $1')
    result = result.slice(1)
    return result.split(' ').join('-').toLowerCase()
  },
  splitCamelCase(str) {
    return str.split(/(?=[A-Z])/).join(' ')
  },
  generateUUID() {
    var d = new Date().getTime() //Timestamp
    var d2 =
      (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0 //Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 //random number between 0 and 16
      if (d > 0) {
        //Use timestamp until depleted
        r = (d + r) % 16 | 0
        d = Math.floor(d / 16)
      } else {
        //Use microseconds since page-load if supported
        r = (d2 + r) % 16 | 0
        d2 = Math.floor(d2 / 16)
      }
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    })
  },
  removeNullAndEmptyValues(obj) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value === null) return acc
      if (value === '') return acc
      if (
        value &&
        value.constructor === Object &&
        Object.keys(this.removeNullAndEmptyValues(value)).length === 0
      )
        return acc
      acc[key] = value
      return acc
    }, {})
  },
  uniqueByField(array, fieldLambda) {
    const seen = new Set()
    return array.filter((item) => {
      if (item == null) return true
      const value = fieldLambda(item)
      if (seen.has(value)) {
        return false
      }
      seen.add(value)
      return true
    })
  },
  copyToClipboard(text) {
    // create a temporary textarea element
    const textarea = document.createElement('textarea')
    textarea.value = text

    // make the textarea invisible
    textarea.style.position = 'fixed'
    textarea.style.top = '-9999px'

    // add the textarea to the document
    document.body.appendChild(textarea)

    // select the text in the textarea
    textarea.select()

    // copy the text to the clipboard
    document.execCommand('copy')

    // remove the textarea from the document
    document.body.removeChild(textarea)
  },
  datetimeToString(datetime) {
    let startDate = datetime.toISOString()
    startDate = startDate.replace(' ', 'T')
    return startDate
  },
  routeIsAdmin() {
    return router.currentRoute.value.matched.find((match) => match.name == 'admin') != undefined
  },
  productIsMain() {
    let product = settings.PRODUCT
    return ['prime', 'cms'].includes(product)
  },
  getTimeDiff(date1, date2) {
    return Math.abs(Math.floor((date2.getTime() - date1.getTime()) / 1000))
  },
  getBiggestTimeDiff(date1, date2) {
    const diffInSeconds = this.getTimeDiff(date1, date2)
    const units = [
      { label: 'year', seconds: 365 * 24 * 60 * 60 },
      { label: 'month', seconds: 30 * 24 * 60 * 60 },
      { label: 'week', seconds: 7 * 24 * 60 * 60 },
      { label: 'day', seconds: 24 * 60 * 60 },
      { label: 'hour', seconds: 60 * 60 },
      { label: 'minute', seconds: 60 },
      { label: 'second', seconds: 1 }
    ]

    for (let unit of units) {
      const amount = Math.floor(diffInSeconds / unit.seconds)
      if (amount >= 1) {
        return `${amount} ${unit.label}${amount > 1 ? 's' : ''}`
      }
    }
    return '0 seconds'
  },
  deepEqual(obj1, obj2) {
    if (obj1 === obj2) return true // Same reference
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) {
      return false // Primitive values or null
    }

    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)
    if (keys1.length !== keys2.length) {
      return false
    }

    for (let key of keys1) {
      if (!keys2.includes(key) || !this.deepEqual(obj1[key], obj2[key])) {
        return false
      }
    }
    return true
  },
  isEmpty(value) {
    return (
      value === '' ||
      value == null ||
      (Array.isArray(value) && value.length === 0) ||
      (value instanceof Map && value.size === 0) ||
      (typeof value === 'object' &&
        !(value instanceof Map) &&
        Object.keys(value || {}).length === 0)
    )
  },
  fixLanDir(text) {
    if (text === null) {
      return ''
    }
    // todo: fix it so it works with html
    return text
    const hasRTL = (x) => /[\u0600-\u06FF]/.test(x)
    const hasLTR = (x) => /[a-zA-Z0-9]/.test(x)
    if (!hasLTR(text) || !hasRTL(text)) {
      return text
    }

    const messageParts = String(text).split(' ')
    const messageResult = []
    for (let i = 0; i < messageParts.length; i++) {
      const messagePart = messageParts[i]

      if (hasRTL(messagePart)) {
        messageResult.push('\u2067' + messagePart + '\u2069')
      } else {
        messageResult.push('\u2066' + messagePart + '\u2069')
      }
    }
    return messageResult.join(' ')
  }
}

//todo: if we needed number translator directive again, uncomment this code

// const observers = new WeakMap();
//
// const farsiNumbersDirective = {
//   mounted(el, binding) {
//     let timeoutId = null;
//     const app = binding.instance;
//
//     const convertTextNodes = (node) => {
//       if (node.nodeType === Node.TEXT_NODE) {
//         node.textContent = filters.EnglishToPersianDigits(node.textContent);
//       } else {
//         node.childNodes.forEach(convertTextNodes);
//       }
//     };
//
//     const runConversion = () => {
//       if (i18n.global.locale === 'fa') {
//         const observer = observers.get(el);
//         if (observer) observer.disconnect();
//
//         convertTextNodes(el);
//
//         if (observer) {
//           observer.observe(el, {
//             childList: true,
//             characterData: true,
//             subtree: true,
//           });
//         }
//       }
//     };
//
//     const observer = new MutationObserver(() => {
//       clearTimeout(timeoutId);
//       timeoutId = setTimeout(runConversion, 50);
//     });
//
//     observer.observe(el, {
//       childList: true,
//       characterData: true,
//       subtree: true,
//     });
//
//     observers.set(el, observer);
//   },
//
//   unmounted(el) {
//     const observer = observers.get(el);
//     if (observer) {
//       observer.disconnect();
//       observers.delete(el);
//     }
//   },
// };
//
// app.directive('farsi-numbers', farsiNumbersDirective);

export const fileUtils = {
  imgTypes: ['png', 'jpg', 'jpeg', 'jfif'],
  docTypes: ['pdf', 'html'],
  textTypes: ['txt'],
  getFileNameWithExtension(path) {
    const lastSlashIndex = path.lastIndexOf('/')
    if (lastSlashIndex !== -1) {
      const fileNameWithExtension = path.substring(lastSlashIndex + 1)
      const parts = fileNameWithExtension.split('.')
      return {
        name: parts.slice(0, parts.length - 1).join('.'),
        extension: parts[parts.length - 1].toLowerCase()
      }
    } else {
      const parts = path.split('.')
      return {
        name: parts.slice(0, parts.length - 1).join('.'),
        extension: parts[parts.length - 1].toLowerCase()
      }
    }
  },
  getExtension(path) {
    const fileNameWithExtension = this.getFileNameWithExtension(path)
    return fileNameWithExtension?.extension
  },
  isImage(type) {
    return this.imgTypes.includes(type.toLowerCase())
  },
  isDocument(type) {
    return this.docTypes.includes(type.toLowerCase())
  },
  isTextFile(type) {
    return this.textTypes.includes(type.toLowerCase())
  }
}

export const numberUtils = {
  numberInWords(amount, asset) {
    return (
      PN.convert(filters.formatDecimal(amount, asset.quantityDecimals)) +
      ' ' +
      this.englishToPersianAssets(asset.symbol)
    )
  },
  englishToPersianAssets(symbol) {
    let persian_assets_map = {
      USDT: 'تتر',
      BLC: 'بالانس',
      USD: 'دلار',
      IRT: 'تومان',
      AED: 'درهم',
      GBP: 'پوند',
      CAD: 'دلار کانادا',
      EUR: 'یورو'
    }
    return persian_assets_map[symbol] ?? symbol
  }
}

export const timeUtils = {
  toCountdown(diffInMs) {
    let countdown = '00:00:00'
    if (diffInMs <= 0) {
      return countdown
    }

    const hours = Math.floor(diffInMs / (1000 * 60 * 60))
      .toString()
      .padStart(2, '0')
    const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60))
      .toString()
      .padStart(2, '0')
    const seconds = Math.floor((diffInMs % (1000 * 60)) / 1000)
      .toString()
      .padStart(2, '0')

    countdown = `${hours}:${minutes}:${seconds}`
    return countdown
  },
  dateToDescription(createdAt) {
    const t = i18n.global.t
    const inputDate = new Date(createdAt)
    const today = new Date()
    const DateStart = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate())
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    const diffTime = todayStart - DateStart
    const diffInDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    if (diffInDays === 0) {
      return t('today')
    } else if (diffInDays === 1) {
      return t('yesterday')
    } else if (diffInDays > 1 && diffInDays <= 30) {
      return t('daysAgo', diffInDays, { count: diffInDays })
    } else {
      const diffInMonths = Math.ceil(diffInDays / 30)
      return t('monthsAgo', diffInMonths, { count: diffInMonths })
    }
  }
}

app.config.globalProperties.$filters = filters

let instance = null

class RouteHistorySingleton {
  constructor() {
    if (instance) {
      return instance
    }
    this.routeHistory = []
    this.scrollMap = new Map()
    this.maxLength = 10
    instance = this
  }

  pushRoute(route, scrollY = undefined) {
    if (!route || !route.fullPath) return

    const last = this.routeHistory[this.routeHistory.length - 1]
    if (!last || last.fullPath !== route.fullPath) {
      this.routeHistory.push(route)
      if (this.routeHistory.length > this.maxLength) {
        this.routeHistory.shift()
      }
    }

    if (typeof scrollY === 'number') {
      this.scrollMap.set(route.fullPath, scrollY)
    }
  }

  setScroll(fullPath, scrollY) {
    if (!fullPath) return
    this.scrollMap.set(fullPath, scrollY)
  }

  getScroll(fullPath) {
    return this.scrollMap.get(fullPath) || 0
  }

  last() {
    return this.routeHistory[this.routeHistory.length - 1]
  }

  pop() {
    return this.routeHistory.pop()
  }

  getAll() {
    return this.routeHistory.slice()
  }

  clear() {
    this.routeHistory = []
    this.scrollMap.clear()
  }
}

const routeHistorySingleton = new RouteHistorySingleton()
const DEFAULT_TITLE = settings.PRODUCT_TITLE

export function restoreScrollWithUseScroll(targetY, opts = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {}

  const { timeoutMs = 7000, pollInterval = 150, tolerance = 2 } = opts

  const { y: scrollY } = useWindowScroll()
  const body = document.body
  const { height: bodyHeight } = useElementSize(body)

  let intervalId = null
  let timeoutId = null
  const imageListeners = new Map()
  let stopped = false

  const cleanup = () => {
    if (stopped) return
    stopped = true
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    imageListeners.forEach((handler, img) => {
      try {
        img.removeEventListener('load', handler)
        img.removeEventListener('error', handler)
      } catch (e) {}
    })
    imageListeners.clear()
  }

  const clampAndScroll = (y) => {
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
    const final = Math.min(y, maxScroll)
    window.scrollTo({ top: final, behavior: 'auto' })
    return final
  }

  const succeeded = () => {
    const cur = window.scrollY || window.pageYOffset || 0
    return (
      Math.abs(cur - targetY) <= tolerance ||
      document.documentElement.scrollHeight - window.innerHeight >= targetY
    )
  }

  nextTick(() => {
    clampAndScroll(targetY)
  })
  if (succeeded()) {
    cleanup()
    return cleanup
  }

  const stopWatch = watch(
    () => bodyHeight.value,
    () => {
      if (stopped) return
      clampAndScroll(targetY)
      if (succeeded()) {
        stopWatch() // stop the watcher
        cleanup()
      }
    }
  )

  intervalId = setInterval(() => {
    if (stopped) return
    clampAndScroll(targetY)
    if (succeeded()) {
      clearInterval(intervalId)
      intervalId = null
      stopWatch()
      cleanup()
    }
  }, pollInterval)

  const imgs = Array.from(document.querySelectorAll('img'))
  imgs.forEach((img) => {
    if (!img.complete) {
      const handler = () => {
        if (stopped) return
        clampAndScroll(targetY)
        if (succeeded()) {
          stopWatch()
          cleanup()
        }
      }
      imageListeners.set(img, handler)
      img.addEventListener('load', handler, { passive: true })
      img.addEventListener('error', handler, { passive: true })
    }
  })

  timeoutId = setTimeout(() => {
    if (stopped) return
    clampAndScroll(targetY) // final attempt
    stopWatch()
    cleanup()
  }, timeoutMs)

  return () => {
    stopWatch()
    cleanup()
  }
}

export const componentState = {
  sectionStatus: null,
  scrollY: 0,
  listOptions: {}
}

export function saveState(sectionVisible, y, options) {
  componentState.sectionStatus = sectionVisible
  componentState.scrollY = y
  componentState.listOptions = options
}

export function getState() {
  return { ...componentState }
}

router.beforeEach((to, from, next) => {
  document.title = DEFAULT_TITLE
  if (to.meta.title) {
    document.title = to.meta.title
  }

  if (from && from.fullPath) {
    routeHistorySingleton.pushRoute(from, typeof window !== 'undefined' ? window.scrollY || 0 : 0)
  }

  routeHistorySingleton.pushRoute(from)
  const { $cookies } = app.config.globalProperties

  if (to.name == 'Login' && $cookies.get('token')) {
    next({ name: 'Home' })
    return
  }

  if (!((to.meta ?? { needAuth: true }).needAuth ?? true)) {
    next()
  } else {
    if ($cookies.get('token') == null) next({ name: 'Login', query: { redirect: to.fullPath } })
    else next()
  }
})

router.afterEach((to, from) => {
  const hist = routeHistorySingleton.routeHistory
  const len = hist.length
  if (
    len >= 2 &&
    hist[len - 2].fullPath === to.fullPath &&
    JSON.stringify(hist[len - 2].params) == JSON.stringify(to.params) &&
    !filters.routeIsAdmin()
  ) {
    User.getMe()
      .then((user) => {
        if (user.isAdmin) {
          const scrollY = routeHistorySingleton.getScroll(to.fullPath) || 0
          // remove the last (we popped back)
          routeHistorySingleton.pop()
          restoreScrollWithUseScroll(scrollY, { timeoutMs: 10000, pollInterval: 120 })
          return
        }
      })
      .catch(() => {})
  } else
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }, 750)
})

export const historyMixin = {
  data() {
    return {
      routeHistory: routeHistorySingleton.routeHistory
    }
  }
}

// Sentry.init({
//   app,
//   dsn:
//     process.env.NODE_ENV == 'production'
//       ? 'https://b6b0e6fea7ee0c48f1aad53748612df1@o4508370794315776.ingest.de.sentry.io/4508370796019792'
//       : '',
//   skipBrowserExtensionCheck: true,
//   integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
//   release: 'first-local',
//   // Tracing
//   tracesSampleRate: 1.0, //  Capture 100% of the transactions
//   // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
//   tracePropagationTargets: [/^https:\/\/yourserver\.io\/api/],
//   // Session Replay
//   replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
//   replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
// })

app.mixin(historyMixin)

axios.defaults.paramsSerializer = (params) => {
  return qs.stringify(app.config.globalProperties.$filters.removeNullAndEmptyValues(params), {
    encode: false, // Prevent percent-encoding
    allowDots: true // Enable dot notation for nested objects
  })
}
axios_include_timezone.defaults.paramsSerializer = axios.defaults.paramsSerializer

axios.defaults.baseURL = settings.API_URL
axios_include_timezone.defaults.baseURL = axios.defaults.baseURL

app.use(createPinia())
app.use(router)
app.use(gtag)
app.use(VueAxios, axios)
app.use(VueAxios, axios_include_timezone)
app.use(VueCookies)
app.component('VOtpInput', VOtpInput)
app.provide('axios', app.config.globalProperties.axios) // provide 'axios'
app.provide('axios_include_timezone', app.config.globalProperties.axios_include_timezone)
app.mount('#app')

const primePhoneNumber = '+971 58 865 9809'

export { axios_include_timezone }
export { primePhoneNumber }
export default app
